import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { auth } from "../firebase";
import { useForm } from "../hooks/useForm";
import { IMAGE_PATHS } from "../constants/config";
import { authService } from "../services/apiService";
import Toast from "../components/Toast";

const INPUT_CLS =
  "w-full px-4 py-2 bg-white/20 border border-white/30 rounded-md text-white text-sm placeholder-white/50 focus:outline-none focus:border-white/60";
const DISABLED_CLS =
  "w-full px-4 py-2 bg-white/10 border border-white/20 rounded-md text-white/60 cursor-not-allowed text-sm";

// ── OTP Popup ─────────────────────────────────────────────────────────────────
const OtpPopup = ({
  mobile,
  onVerified,
  onClose,
  confirmResult,
  resendTimer,
  onResend,
  otpLoading,
}) => {
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const inputs = useRef([]);

  const handleChange = (val, idx) => {
    if (!/^\d?$/.test(val)) return;
    const arr = otp.split("");
    arr[idx] = val;
    const next = arr.join("").slice(0, 6);
    setOtp(next);
    if (val && idx < 5) inputs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0)
      inputs.current[idx - 1]?.focus();
  };

  const verify = async () => {
    if (otp.length !== 6) {
      setError("Enter all 6 digits");
      return;
    }
    setError("");
    setVerifying(true);
    try {
      await confirmResult.confirm(otp);
      onVerified();
    } catch {
      setError("Invalid OTP. Please try again.");
      setOtp("");
      inputs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.85 }}
        transition={{ duration: 0.25 }}
        className="bg-gray-900 border border-white/20 rounded-2xl px-8 py-7 w-full max-w-sm shadow-2xl text-center"
      >
        <div className="w-14 h-14 rounded-full bg-blue-500/20 border border-blue-400/40 flex items-center justify-center mx-auto mb-4">
          <i className="fas fa-mobile-alt text-blue-400 text-2xl" />
        </div>
        <h3 className="text-white text-lg font-bold mb-1">Verify Mobile</h3>
        <p className="text-white/50 text-xs mb-5">
          OTP sent to{" "}
          <span className="text-white font-semibold">+91 {mobile}</span>
        </p>
        <div className="flex justify-center gap-2 mb-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <input
              key={i}
              ref={(el) => (inputs.current[i] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={otp[i] || ""}
              onChange={(e) => handleChange(e.target.value, i)}
              onKeyDown={(e) => handleKeyDown(e, i)}
              className="w-10 h-12 text-center text-white text-lg font-bold bg-white/10 border border-white/30 rounded-lg focus:outline-none focus:border-blue-400 focus:bg-white/20 transition"
            />
          ))}
        </div>
        {error && <p className="text-red-400 text-xs mb-3">{error}</p>}
        <button
          onClick={verify}
          disabled={verifying || otp.length !== 6}
          className="w-full py-2.5 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-bold text-sm disabled:opacity-50 transition mb-3"
        >
          {verifying ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2" />
              Verifying...
            </>
          ) : (
            "Verify OTP"
          )}
        </button>
        <div className="flex items-center justify-between text-xs">
          <button
            onClick={onClose}
            className="text-white/40 hover:text-white/70 transition"
          >
            Cancel
          </button>
          {resendTimer > 0 ? (
            <span className="text-white/40">Resend in {resendTimer}s</span>
          ) : (
            <button
              onClick={onResend}
              disabled={otpLoading}
              className="text-blue-400 hover:text-blue-300 font-semibold transition disabled:opacity-50"
            >
              {otpLoading ? "Sending..." : "Resend OTP"}
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

// ── Main Signup ───────────────────────────────────────────────────────────────
const Signup = () => {
  const navigate = useNavigate();
  const { formData, handleNestedChange } = useForm({
    address: {
      addressLine1: "",
      addressLine2: "",
      pinCode: "",
      nearBy: "",
      houseNo: "",
      country: "",
      state: "",
      distict: "",
    },
    personalDetails: { name: "", mobile: "", religion: "", email: "" },
    login: { userName: "", password: "" },
  });

  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [companyCode, setCompanyCode] = useState("");

  const [otpVerified, setOtpVerified] = useState(false);
  const [showOtpPopup, setShowOtpPopup] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [confirmResult, setConfirmResult] = useState(null);
  const [resendTimer, setResendTimer] = useState(0);
  const recaptchaRef = useRef(null);
  const timerRef = useRef(null);

  //useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);

      if (recaptchaRef.current) {
        recaptchaRef.current.clear();
        recaptchaRef.current = null;
      }
    };
  }, []);

  const generateCode = (name, mobile) => {
    const prefix = name.trim().replace(/\s+/g, "").slice(0, 3).toUpperCase();
    const suffix = mobile.slice(-4);
    return prefix && suffix ? `${prefix}_${suffix}` : prefix || "";
  };

  const handleCompanyNameChange = (val) => {
    setCompanyName(val);
    setCompanyCode(generateCode(val, formData.login.userName));
  };

  //const setupRecaptcha = () => {
  //  if (recaptchaRef.current) { recaptchaRef.current.clear(); recaptchaRef.current = null; }
  //  recaptchaRef.current = new RecaptchaVerifier(auth, "recaptcha-container", { size: "invisible", callback: () => {} });
  //  return recaptchaRef.current;
  //};

  const setupRecaptcha = () => {
    if (!recaptchaRef.current) {
      recaptchaRef.current = new RecaptchaVerifier(
        auth,
        "recaptcha-container",
        {
          size: "invisible",
          callback: () => {},
        },
      );
    }
    return recaptchaRef.current;
  };

  const startResendTimer = () => {
    setResendTimer(30);
    timerRef.current = setInterval(() => {
      setResendTimer((t) => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
  };

  const sendOtp = async () => {
    const mobile = formData.login.userName.trim();

    if (!window.navigator.onLine) {
      setOtpError("No internet connection");
      return;
    }

    if (mobile.length !== 10) {
      setOtpError("Enter a valid 10-digit mobile number first");
      return;
    }
    setOtpError("");
    setOtpLoading(true);
    try {
      const verifier = setupRecaptcha();
      const result = await signInWithPhoneNumber(
        auth,
        `+91${mobile}`,
        verifier,
      );
      setConfirmResult(result);
      setShowOtpPopup(true);
      startResendTimer();
    } catch (err) {
      console.log("FULL ERROR 👉", err);
      console.log("ERROR CODE 👉", err.code);
      console.log("ERROR MESSAGE 👉", err.message);
      console.log("CUSTOM DATA 👉", err.customData);

      // 👇 ADD THIS LINE (VERY IMPORTANT)
      if (err?.customData?._tokenResponse) {
        console.log("TOKEN RESPONSE 👉", err.customData._tokenResponse);
      }

      setOtpError(err.message);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResend = async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    await sendOtp();
  };

  const handleVerified = () => {
    setOtpVerified(true);
    setShowOtpPopup(false);
  };

  const validate = () => {
    //if (!otpVerified) {
    //  setToast({
    //    type: "error",
    //    message: "Please verify your mobile number with OTP",
    //  });
    //  return false;
    //}
    if (
      !formData.login.userName.trim() ||
      formData.login.userName.length !== 10
    ) {
      setToast({ type: "error", message: "Mobile Number must be 10 digits" });
      return false;
    }
    if (!formData.login.password.trim()) {
      setToast({ type: "error", message: "Password is required" });
      return false;
    }
    if (!companyName.trim()) {
      setToast({ type: "error", message: "Company Name is required" });
      return false;
    }
    if (!companyCode.trim()) {
      setToast({ type: "error", message: "Company Code is required" });
      return false;
    }
    if (!formData.personalDetails.name.trim()) {
      setToast({ type: "error", message: "Full Name is required" });
      return false;
    }
    if (!formData.personalDetails.email.trim()) {
      setToast({ type: "error", message: "Email is required" });
      return false;
    }
    if (!formData.address.addressLine1.trim()) {
      setToast({ type: "error", message: "Address Line 1 is required" });
      return false;
    }
    if (!formData.address.houseNo.trim()) {
      setToast({ type: "error", message: "House No is required" });
      return false;
    }
    if (!formData.address.distict.trim()) {
      setToast({ type: "error", message: "District is required" });
      return false;
    }
    if (!formData.address.state.trim()) {
      setToast({ type: "error", message: "State is required" });
      return false;
    }
    if (!formData.address.country.trim()) {
      setToast({ type: "error", message: "Country is required" });
      return false;
    }
    if (!formData.address.pinCode.trim()) {
      setToast({ type: "error", message: "Pincode is required" });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setToast(null);
    if (!validate()) return;
    setLoading(true);
    try {
      await authService.signup({
        client: { clientName: companyName, clientCode: companyCode },
        login: {
          mobileNo: formData.login.userName,
          password: formData.login.password,
        },
        personalDetails: {
          name: formData.personalDetails.name,
          mobile: formData.personalDetails.mobile,
          religion: formData.personalDetails.religion,
          email: formData.personalDetails.email,
        },
        address: formData.address,
      });
      setToast({
        type: "success",
        message: "Account created successfully! Redirecting to login...",
      });
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      const msg =
        err.response?.data?.errorMessage ||
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        "Signup failed. Try again.";
      setToast({ type: "error", message: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen w-screen flex items-center justify-center bg-black relative overflow-hidden m-0 p-0">
      <div className="absolute inset-0">
        <img
          src={IMAGE_PATHS.BACKGROUND}
          alt="Background"
          className="w-full h-full object-cover"
        />
      </div>

     {/* <div id="recaptcha-container" />*/}

      {/* Toast */}
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}

      {/* OTP Popup 
      <AnimatePresence>
        {showOtpPopup && (
          <OtpPopup
            mobile={formData.login.userName}
            confirmResult={confirmResult}
            resendTimer={resendTimer}
            otpLoading={otpLoading}
            onVerified={handleVerified}
            onClose={() => setShowOtpPopup(false)}
            onResend={handleResend}
          />
        )}
      </AnimatePresence>*/}

      <motion.div
        className="relative z-10 bg-black bg-opacity-50 backdrop-blur-sm rounded-xl px-10 py-6 shadow-xl border border-white border-opacity-10 w-[95%] max-w-5xl"
        whileHover={{ scale: 1.02 }}
        transition={{ duration: 0.3 }}
      >
        <h2 className="text-center text-2xl font-bold mb-5 text-white">
          Create Account
        </h2>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* ── Login Info ── */}
          <div>
            <h3 className="text-white text-base font-semibold mb-2">
              Login Info
            </h3>
            <div className="grid grid-cols-4 gap-4 items-center">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Mobile Number"
                  value={formData.login.userName}
                  disabled={otpVerified}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                    handleNestedChange("login", "userName", val);
                    handleNestedChange("personalDetails", "mobile", val);
                    setCompanyCode(generateCode(companyName, val));
                    setOtpVerified(false);
                    setOtpError("");
                  }}
                  className={`w-full pl-4 pr-10 py-2 bg-white/20 border rounded-md text-white text-sm placeholder-white/50 focus:outline-none ${otpVerified ? "border-green-400/60 bg-green-500/10" : "border-white/30"}`}
                />
                {/*{otpVerified ? (
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-green-400 text-sm">
                    <i className="fas fa-check-circle" />
                  </span>
                ) : (
                  <button
                    type="button"
                    //onClick={sendOtp}
                    disabled={otpLoading}
                    title="Send OTP"
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-50 transition"
                  >
                    {otpLoading ? (
                      <i className="fas fa-spinner fa-spin text-xs" />
                    ) : (
                      <i className="fas fa-paper-plane text-xs" />
                    )}
                  </button>
                )}*/}
              </div>
              <input
                type="password"
                placeholder="Password"
                value={formData.login.password}
                onChange={(e) =>
                  handleNestedChange("login", "password", e.target.value)
                }
                className={INPUT_CLS}
              />
            </div>
            {otpError && (
              <p className="text-red-400 text-xs mt-1">{otpError}</p>
            )}
            {otpVerified && (
              <p className="text-green-400 text-xs mt-1">
                <i className="fas fa-check-circle mr-1" />
                Mobile number verified
              </p>
            )}
          </div>

          {/* ── Client Info ── */}
          <div>
            <h3 className="text-white text-base font-semibold mb-2">
              Client Info
            </h3>
            <div className="grid grid-cols-4 gap-4">
              <input
                type="text"
                placeholder="Company Name"
                value={companyName}
                onChange={(e) => handleCompanyNameChange(e.target.value)}
                className={INPUT_CLS}
              />
              <input
                type="text"
                placeholder="Company Code"
                value={companyCode}
                readOnly
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-md text-white/60 cursor-not-allowed font-mono tracking-wider text-sm"
              />
            </div>
            <p className="text-white/50 text-xs mt-1">
              Code is auto-generated. Must be unique.
            </p>
          </div>

          {/* ── Personal Details ── */}
          <div>
            <h3 className="text-white text-base font-semibold mb-2">
              Personal Details
            </h3>
            <div className="grid grid-cols-4 gap-4">
              <input
                type="text"
                placeholder="Full Name"
                value={formData.personalDetails.name}
                onChange={(e) =>
                  handleNestedChange("personalDetails", "name", e.target.value)
                }
                className={INPUT_CLS}
              />
              <input
                type="text"
                placeholder="Mobile"
                value={formData.personalDetails.mobile}
                disabled
                className={DISABLED_CLS}
              />
              <input
                type="text"
                placeholder="Religion"
                value={formData.personalDetails.religion}
                onChange={(e) =>
                  handleNestedChange(
                    "personalDetails",
                    "religion",
                    e.target.value,
                  )
                }
                className={INPUT_CLS}
              />
              <input
                type="email"
                placeholder="Email"
                value={formData.personalDetails.email}
                onChange={(e) =>
                  handleNestedChange("personalDetails", "email", e.target.value)
                }
                className={INPUT_CLS}
              />
            </div>
          </div>

          {/* ── Address ── */}
          <div>
            <h3 className="text-white text-base font-semibold mb-2">Address</h3>
            <div className="grid grid-cols-4 gap-4">
              <input
                type="text"
                placeholder="Address Line 1"
                value={formData.address.addressLine1}
                onChange={(e) =>
                  handleNestedChange("address", "addressLine1", e.target.value)
                }
                className={INPUT_CLS}
              />
              <input
                type="text"
                placeholder="Address Line 2"
                value={formData.address.addressLine2}
                onChange={(e) =>
                  handleNestedChange("address", "addressLine2", e.target.value)
                }
                className={INPUT_CLS}
              />
              <input
                type="text"
                placeholder="House No"
                value={formData.address.houseNo}
                onChange={(e) =>
                  handleNestedChange("address", "houseNo", e.target.value)
                }
                className={INPUT_CLS}
              />
              <input
                type="text"
                placeholder="Nearby"
                value={formData.address.nearBy}
                onChange={(e) =>
                  handleNestedChange("address", "nearBy", e.target.value)
                }
                className={INPUT_CLS}
              />
              <input
                type="text"
                placeholder="District"
                value={formData.address.distict}
                onChange={(e) =>
                  handleNestedChange("address", "distict", e.target.value)
                }
                className={INPUT_CLS}
              />
              <input
                type="text"
                placeholder="State"
                value={formData.address.state}
                onChange={(e) =>
                  handleNestedChange("address", "state", e.target.value)
                }
                className={INPUT_CLS}
              />
              <input
                type="text"
                placeholder="Country"
                value={formData.address.country}
                onChange={(e) =>
                  handleNestedChange("address", "country", e.target.value)
                }
                className={INPUT_CLS}
              />
              <input
                type="text"
                placeholder="Pincode"
                value={formData.address.pinCode}
                onChange={(e) =>
                  handleNestedChange("address", "pinCode", e.target.value)
                }
                className={INPUT_CLS}
              />
            </div>
          </div>

          {/*<button
            type="submit"
            disabled={loading || !otpVerified}
            className="w-full bg-white text-black font-bold py-2 rounded-md hover:bg-opacity-80 transition disabled:opacity-50"
          >
            {loading ? "Saving..." : "Sign Up"}
          </button>*/}
          <button
            type="submit"
            
            className="w-full bg-white text-black font-bold py-2 rounded-md hover:bg-opacity-80 transition disabled:opacity-50"
          >
            {loading ? "Saving..." : "Sign Up"}
          </button>
        </form>

        <div className="text-center mt-2">
          <span className="text-white text-sm">Already have an account? </span>
          <Link
            to="/login"
            className="text-blue-400 hover:text-blue-200 font-semibold transition"
          >
            Login
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default Signup;
